import React, { createContext, useContext, useState } from "react"

interface PublishedBreadcrumbTitle {
  /** The route segment of the resource whose crumb this replaces. */
  resourceId: string
  title: React.ReactNode
}

// The setter lives in its own context so a page that publishes a title does
// not re-render when the title it published changes.
const BreadcrumbTitleContext = createContext<PublishedBreadcrumbTitle | null>(
  null,
)
const SetBreadcrumbTitleContext = createContext<
  (published: PublishedBreadcrumbTitle | null) => void
>(() => {})

interface BreadcrumbTitleProviderProps {
  children: React.ReactNode
}

export const BreadcrumbTitleProvider: React.FC<
  BreadcrumbTitleProviderProps
> = ({ children }) => {
  const [published, setPublished] = useState<PublishedBreadcrumbTitle | null>(
    null,
  )

  return (
    <SetBreadcrumbTitleContext.Provider value={setPublished}>
      <BreadcrumbTitleContext.Provider value={published}>
        {children}
      </BreadcrumbTitleContext.Provider>
    </SetBreadcrumbTitleContext.Provider>
  )
}

/**
 * The node a page published to replace its resource's name crumb, such as an
 * editable title. The provider outlives page swaps and a page clears its title
 * only after the next page has rendered, so match `resourceId` against the
 * crumb before rendering the title.
 */
export const useBreadcrumbTitle = () => useContext(BreadcrumbTitleContext)

export const useSetBreadcrumbTitleContext = () =>
  useContext(SetBreadcrumbTitleContext)
